import { useState, useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { Search, X, Star, Plus, UserPlus, ChevronDown, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { useBrowserDetection } from "@/hooks/use-browser-detection";
import type { Act, ActWithParticipants, Participant } from "@shared/schema";

interface ActFormModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  organisationId: number;
  act?: ActWithParticipants;
}

const GENRES = [
  "Rock", "Pop", "Jazz", "Blues", "Classical", "Country", "Electronic",
  "Hip Hop", "R&B", "Folk", "Metal", "Reggae", "Soul", "Funk", "Latin"
];

export function ActFormModal({ open, onOpenChange, organisationId, act }: ActFormModalProps) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedParticipants, setSelectedParticipants] = useState<Participant[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedGenres, setSelectedGenres] = useState<string[]>([]);
  const [rating, setRating] = useState(0);
  const [showNewParticipant, setShowNewParticipant] = useState(false);
  const [newParticipantName, setNewParticipantName] = useState("");
  const [newParticipantEmail, setNewParticipantEmail] = useState("");
  const [newParticipantPhone, setNewParticipantPhone] = useState("");
  const [newParticipantInstagram, setNewParticipantInstagram] = useState("");
  const [showOptionalFields, setShowOptionalFields] = useState(false);
  
  // Detect browser for transparency handling
  const { isChrome, isMobileSafari } = useBrowserDetection();

  // Form state
  const [formData, setFormData] = useState({
    name: "", // Act name (required if multiple participants)
    actType: "individual" as "individual" | "duo" | "band" | "ensemble",
    minimumFee: "",
    preferredFee: "",
    technicalRequirements: "",
    bio: "",
    website: "",
    spotifyUrl: "",
    youtubeUrl: "",
    soundcloudUrl: "",
    notes: "",
  });

  // Load existing act data if editing
  useEffect(() => {
    if (act) {
      setFormData({
        name: act.name || "",
        actType: (act.actType || "individual") as "individual" | "duo" | "band" | "ensemble",
        minimumFee: act.minimumFee ? (act.minimumFee / 100).toString() : "",
        preferredFee: act.preferredFee ? (act.preferredFee / 100).toString() : "",
        technicalRequirements: act.technicalRequirements || "",
        bio: act.bio || "",
        website: act.website || "",
        spotifyUrl: act.spotifyUrl || "",
        youtubeUrl: act.youtubeUrl || "",
        soundcloudUrl: act.soundcloudUrl || "",
        notes: act.notes || "",
      });
      setSelectedGenres(act.genre || []);
      setRating(act.rating || 0);
      // Set selected participants from existing act
      if (act.participants) {
        setSelectedParticipants(act.participants.map(ap => ap.participant));
      }
      // Auto-expand optional fields if any have data
      if (act.bio || act.website || act.spotifyUrl || act.youtubeUrl || act.soundcloudUrl || act.notes) {
        setShowOptionalFields(true);
      }
    }
  }, [act]);

  // Fetch all participants for the organisation
  const { data: allParticipants = [] } = useQuery<Participant[]>({
    queryKey: [`/api/participants?organisationId=${organisationId}`],
    queryFn: async () => {
      const response = await fetch(`/api/participants?organisationId=${organisationId}`);
      if (!response.ok) throw new Error("Failed to fetch participants");
      return response.json();
    },
    enabled: open,
  });

  // Fetch existing acts to check for duplicates
  const { data: existingActs = [] } = useQuery<ActWithParticipants[]>({
    queryKey: [`/api/acts?organisationId=${organisationId}`],
    queryFn: async () => {
      const response = await fetch(`/api/acts?organisationId=${organisationId}`);
      if (!response.ok) throw new Error("Failed to fetch acts");
      return response.json();
    },
    enabled: open,
  });

  // Get set of participant IDs that are already in acts
  const participantsInActs = new Set(
    existingActs
      .filter((a: ActWithParticipants) => a.active)
      .flatMap((a: ActWithParticipants) => 
        a.participants?.map(ap => ap.participantId) || []
      )
  );

  // Filter participants based on search term and exclude already selected
  const filteredParticipants = (() => {
    if (!allParticipants) return [];
    
    const selectedIds = new Set(selectedParticipants.map(p => p.id));
    
    let filtered = allParticipants.filter(p => !selectedIds.has(p.id));
    
    if (searchTerm.trim().length > 0) {
      filtered = filtered.filter((participant: Participant) => 
        participant.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    return filtered.sort((a: Participant, b: Participant) => a.name.localeCompare(b.name));
  })();

  // Create/update act mutation
  const mutation = useMutation({
    mutationFn: async (data: any) => {
      const url = act 
        ? `/api/acts/${act.id}`
        : `/api/acts?organisationId=${organisationId}`;
      
      const response = await fetch(url, {
        method: act ? "PUT" : "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || "Failed to save act");
      }

      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/acts`] });
      toast({
        title: act ? "Act Updated" : "Act Created",
        description: act ? "Act details have been updated." : "New act has been added to your roster.",
      });
      onOpenChange(false);
      resetForm();
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const handleSubmit = () => {
    // Validation: if multiple participants, must have act name
    if (selectedParticipants.length > 1 && !formData.name.trim()) {
      toast({
        title: "Act Name Required",
        description: "Acts with multiple participants must have a name.",
        variant: "destructive",
      });
      return;
    }

    const data: any = {
      name: formData.name || undefined,
      actType: formData.actType,
      genre: selectedGenres.length > 0 ? selectedGenres : undefined,
      minimumFee: formData.minimumFee ? Math.round(parseFloat(formData.minimumFee) * 100) : undefined,
      preferredFee: formData.preferredFee ? Math.round(parseFloat(formData.preferredFee) * 100) : undefined,
      technicalRequirements: formData.technicalRequirements || undefined,
      bio: formData.bio || undefined,
      website: formData.website || undefined,
      spotifyUrl: formData.spotifyUrl || undefined,
      youtubeUrl: formData.youtubeUrl || undefined,
      soundcloudUrl: formData.soundcloudUrl || undefined,
      rating: rating || undefined,
      notes: formData.notes || undefined,
    };

    if (!act) {
      // Separate existing participants from new ones (negative IDs are temporary)
      const existingParticipants = selectedParticipants.filter(p => p.id > 0);
      const newParticipants = selectedParticipants.filter(p => p.id < 0);
      
      // For new acts, include existing participant IDs
      data.participantIds = existingParticipants.map(p => p.id);
      
      // Include data for new participants to be created
      if (newParticipants.length > 0 || newParticipantName) {
        data.participantData = [
          ...newParticipants.map(p => ({ 
            name: p.name,
            email: p.email || undefined,
            phone: p.phone || undefined,
            instagram: p.instagram || undefined
          })),
          ...(newParticipantName ? [{ 
            name: newParticipantName,
            email: newParticipantEmail || undefined,
            phone: newParticipantPhone || undefined,
            instagram: newParticipantInstagram || undefined
          }] : [])
        ];
      }
    } else {
      // For existing acts, we need to update participants separately
      // Include the participant updates in the data
      const existingParticipants = selectedParticipants.filter(p => p.id > 0);
      const newParticipants = selectedParticipants.filter(p => p.id < 0);
      
      data.participantIds = existingParticipants.map(p => p.id);
      if (newParticipants.length > 0) {
        data.participantData = newParticipants.map(p => ({ 
          name: p.name,
          email: p.email || undefined,
          phone: p.phone || undefined,
          instagram: p.instagram || undefined
        }));
      }
    }

    console.log('Submitting act data:', JSON.stringify(data, null, 2));
    mutation.mutate(data);
  };

  const handleParticipantSelect = (participant: Participant) => {
    setSelectedParticipants(prev => [...prev, participant]);
    setSearchTerm("");
    setShowDropdown(false);
  };

  const handleRemoveParticipant = (participantId: number) => {
    setSelectedParticipants(prev => prev.filter(p => p.id !== participantId));
  };

  const handleAddNewParticipant = () => {
    if (!newParticipantName.trim()) return;
    
    // Create a temporary participant object with contact details
    const tempParticipant: Participant = {
      id: -Date.now(), // Negative ID for temporary participants
      name: newParticipantName,
      organisationId,
      email: newParticipantEmail || null,
      phone: newParticipantPhone || null,
      instagram: newParticipantInstagram || null,
      lookingForBand: false,
      createdAt: new Date(),
      updatedAt: new Date(),
      createdBy: "system",
      updatedBy: "system",
    };
    
    setSelectedParticipants(prev => [...prev, tempParticipant]);
    setNewParticipantName("");
    setNewParticipantEmail("");
    setNewParticipantPhone("");
    setNewParticipantInstagram("");
    setShowNewParticipant(false);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      setShowDropdown(false);
    };
    
    if (showDropdown) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [showDropdown]);

  const resetForm = () => {
    setSearchTerm("");
    setSelectedParticipants([]);
    setShowNewParticipant(false);
    setNewParticipantName("");
    setNewParticipantEmail("");
    setNewParticipantPhone("");
    setNewParticipantInstagram("");
    setShowDropdown(false);
    setSelectedGenres([]);
    setRating(0);
    setShowOptionalFields(false);
    setFormData({
      name: "",
      actType: "individual",
      minimumFee: "",
      preferredFee: "",
      technicalRequirements: "",
      bio: "",
      website: "",
      spotifyUrl: "",
      youtubeUrl: "",
      soundcloudUrl: "",
      notes: "",
    });
  };

  const toggleGenre = (genre: string) => {
    setSelectedGenres(prev =>
      prev.includes(genre)
        ? prev.filter(g => g !== genre)
        : [...prev, genre]
    );
  };

  // Auto-set act type based on number of participants
  useEffect(() => {
    if (selectedParticipants.length === 1) {
      setFormData(prev => ({ ...prev, actType: "individual" }));
    } else if (selectedParticipants.length === 2) {
      setFormData(prev => ({ ...prev, actType: "duo" }));
    } else if (selectedParticipants.length > 2) {
      setFormData(prev => ({ ...prev, actType: "band" }));
    }
  }, [selectedParticipants]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[95vw] max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{act ? "Edit Act" : "Add New Act"}</DialogTitle>
          <DialogDescription>
            {act ? "Update act details and information." : "Create a new act. Add one or more participants to form the act."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Act Name (required for multiple participants) */}
          <div>
            <Label htmlFor="name">
              Act Name {selectedParticipants.length > 1 && <span className="text-red-500">*</span>}
            </Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              placeholder={selectedParticipants.length > 1 ? "Required for multiple members (e.g., 'BlueShell')" : "Optional for solo acts"}
              className={selectedParticipants.length > 1 && !formData.name ? "border-red-500" : ""}
            />
            {selectedParticipants.length > 1 && !formData.name && (
              <p className="text-xs text-red-500 mt-1">Acts with multiple members must have a name</p>
            )}
          </div>

          {/* Participant Selection */}
          <div>
            <Label>Act Members</Label>
              
              {/* Selected participants */}
              {selectedParticipants.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-3">
                  {selectedParticipants.map((participant, index) => (
                    <Badge key={participant.id} variant="secondary" className="py-1">
                      {participant.name}
                      {index === 0 && selectedParticipants.length > 1 && (
                        <span className="ml-1 text-xs">(primary)</span>
                      )}
                      <button
                        type="button"
                        onClick={() => handleRemoveParticipant(participant.id)}
                        className="ml-2 hover:text-red-500"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              )}

              {/* Search for participants */}
              <div className="relative" onClick={(e) => e.stopPropagation()}>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    value={searchTerm}
                    onChange={(e) => {
                      setSearchTerm(e.target.value);
                      setShowDropdown(true);
                    }}
                    onFocus={() => setShowDropdown(true)}
                    placeholder="Search for participants to add..."
                    className="pl-10"
                  />
                </div>

                {/* Dropdown Results */}
                {showDropdown && filteredParticipants.length > 0 && (
                  <div 
                    className={cn(
                      "absolute z-50 w-full mt-1 rounded-xl shadow-2xl max-h-60 overflow-auto border",
                      isChrome || isMobileSafari
                        ? 'bg-white border-gray-200' 
                        : 'bg-white/95 backdrop-blur-xl border-white/30'
                    )}
                    onClick={(e) => e.stopPropagation()}
                  >
                    {filteredParticipants.map((participant: Participant) => {
                      const isInAnotherAct = participantsInActs.has(participant.id);
                      return (
                        <div
                          key={participant.id}
                          onClick={() => {
                            if (!isInAnotherAct) {
                              handleParticipantSelect(participant);
                            }
                          }}
                          className={cn(
                            "px-4 py-3 border-b border-white/30 last:border-b-0",
                            isInAnotherAct 
                              ? "opacity-50 cursor-not-allowed bg-white/20" 
                              : "hover:bg-white/40 cursor-pointer"
                          )}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex-1">
                              <div className="font-medium text-slate-900 flex items-center gap-2">
                                {participant.name}
                                {isInAnotherAct && (
                                  <Badge variant="outline" className="text-xs bg-purple-50 text-purple-700 border-purple-200">
                                    In another act
                                  </Badge>
                                )}
                              </div>
                              {participant.email && (
                                <div className="text-sm text-slate-500">
                                  {participant.email}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Add new participant button */}
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setShowNewParticipant(true)}
                className="mt-2"
              >
                <UserPlus className="h-4 w-4 mr-2" />
                Create New Participant
              </Button>

              {/* New participant form */}
              {showNewParticipant && (
                <div className="mt-3 p-3 border rounded-lg bg-accent/20 space-y-3">
                  <div className="space-y-2">
                    <div>
                      <Label htmlFor="new-participant-name" className="text-sm">Name *</Label>
                      <Input
                        id="new-participant-name"
                        placeholder="Participant name..."
                        value={newParticipantName}
                        onChange={(e) => setNewParticipantName(e.target.value)}
                      />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      <div>
                        <Label htmlFor="new-participant-email" className="text-sm">Email</Label>
                        <Input
                          id="new-participant-email"
                          type="email"
                          placeholder="email@example.com"
                          value={newParticipantEmail}
                          onChange={(e) => setNewParticipantEmail(e.target.value)}
                        />
                      </div>
                      <div>
                        <Label htmlFor="new-participant-phone" className="text-sm">Phone</Label>
                        <Input
                          id="new-participant-phone"
                          type="tel"
                          placeholder="+1234567890"
                          value={newParticipantPhone}
                          onChange={(e) => setNewParticipantPhone(e.target.value)}
                        />
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="new-participant-instagram" className="text-sm">Instagram</Label>
                      <Input
                        id="new-participant-instagram"
                        placeholder="@username"
                        value={newParticipantInstagram}
                        onChange={(e) => setNewParticipantInstagram(e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="flex gap-2 justify-end">
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        setShowNewParticipant(false);
                        setNewParticipantName("");
                        setNewParticipantEmail("");
                        setNewParticipantPhone("");
                        setNewParticipantInstagram("");
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      onClick={handleAddNewParticipant}
                      disabled={!newParticipantName.trim()}
                      className="bg-blue-600 hover:bg-blue-700 text-white"
                    >
                      Add Participant
                    </Button>
                  </div>
                </div>
              )}
            </div>

          {/* Act Details */}
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="actType">Act Type</Label>
                <Select
                  value={formData.actType}
                  onValueChange={(value: any) => setFormData(prev => ({ ...prev, actType: value }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="individual">Individual</SelectItem>
                    <SelectItem value="duo">Duo</SelectItem>
                    <SelectItem value="band">Band</SelectItem>
                    <SelectItem value="ensemble">Ensemble</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Rating</Label>
                <div className="flex gap-1 mt-2">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      type="button"
                      onClick={() => setRating(star)}
                      className="p-1"
                    >
                      <Star
                        className={`h-5 w-5 ${
                          star <= rating
                            ? "fill-yellow-400 text-yellow-400"
                            : "text-gray-300"
                        }`}
                      />
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div>
              <Label>Genres</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {GENRES.map((genre) => (
                  <Badge
                    key={genre}
                    variant={selectedGenres.includes(genre) ? "default" : "outline"}
                    className="cursor-pointer"
                    onClick={() => toggleGenre(genre)}
                  >
                    {genre}
                  </Badge>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="minimumFee">Minimum Fee (£)</Label>
                <Input
                  id="minimumFee"
                  type="number"
                  step="0.01"
                  value={formData.minimumFee}
                  onChange={(e) => setFormData(prev => ({ ...prev, minimumFee: e.target.value }))}
                />
              </div>
              <div>
                <Label htmlFor="preferredFee">Preferred Fee (£)</Label>
                <Input
                  id="preferredFee"
                  type="number"
                  step="0.01"
                  value={formData.preferredFee}
                  onChange={(e) => setFormData(prev => ({ ...prev, preferredFee: e.target.value }))}
                />
              </div>
            </div>

            <div>
              <Label htmlFor="technicalRequirements">Technical Requirements</Label>
              <Textarea
                id="technicalRequirements"
                value={formData.technicalRequirements}
                onChange={(e) => setFormData(prev => ({ ...prev, technicalRequirements: e.target.value }))}
                rows={2}
              />
            </div>

            {/* Collapsible section for optional fields */}
            <div className="border rounded-lg">
              <button
                type="button"
                onClick={() => setShowOptionalFields(!showOptionalFields)}
                className="w-full flex items-center justify-between p-3 hover:bg-gray-50 transition-colors"
              >
                <span className="font-medium text-sm">Additional Information (Optional)</span>
                {showOptionalFields ? (
                  <ChevronDown className="h-4 w-4 text-gray-500" />
                ) : (
                  <ChevronRight className="h-4 w-4 text-gray-500" />
                )}
              </button>
              
              {showOptionalFields && (
                <div className="p-4 border-t space-y-4">
                  <div>
                    <Label htmlFor="bio">Biography</Label>
                    <Textarea
                      id="bio"
                      value={formData.bio}
                      onChange={(e) => setFormData(prev => ({ ...prev, bio: e.target.value }))}
                      rows={3}
                      placeholder="Artist biography..."
                    />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="website">Website</Label>
                      <Input
                        id="website"
                        type="url"
                        value={formData.website}
                        onChange={(e) => setFormData(prev => ({ ...prev, website: e.target.value }))}
                        placeholder="https://example.com"
                      />
                    </div>
                    <div>
                      <Label htmlFor="spotifyUrl">Spotify</Label>
                      <Input
                        id="spotifyUrl"
                        type="url"
                        value={formData.spotifyUrl}
                        onChange={(e) => setFormData(prev => ({ ...prev, spotifyUrl: e.target.value }))}
                        placeholder="Spotify artist URL"
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="youtubeUrl">YouTube</Label>
                      <Input
                        id="youtubeUrl"
                        type="url"
                        value={formData.youtubeUrl}
                        onChange={(e) => setFormData(prev => ({ ...prev, youtubeUrl: e.target.value }))}
                        placeholder="YouTube channel URL"
                      />
                    </div>
                    <div>
                      <Label htmlFor="soundcloudUrl">SoundCloud</Label>
                      <Input
                        id="soundcloudUrl"
                        type="url"
                        value={formData.soundcloudUrl}
                        onChange={(e) => setFormData(prev => ({ ...prev, soundcloudUrl: e.target.value }))}
                        placeholder="SoundCloud profile URL"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="notes">Internal Notes</Label>
                    <Textarea
                      id="notes"
                      value={formData.notes}
                      onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                      rows={2}
                      placeholder="Private notes about this act (not visible to the act)..."
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <DialogFooter className="flex-col-reverse sm:flex-row gap-2 sm:gap-0">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="w-full sm:w-auto">
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={mutation.isPending || (!act && selectedParticipants.length === 0)}
            className="w-full sm:w-auto"
          >
            {mutation.isPending ? "Saving..." : act ? "Update Act" : "Add Act"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}